import React, { MutableRefObject, useState } from 'react';
import styled, { css, useTheme } from 'styled-components/macro';
import { useParams } from 'react-router-dom';
// assets
import { MdClose, MdComment, MdVideocam } from 'react-icons/md';
// constants
import {
  VideoReactionsDictionary,
  VIDEO_REACTION_TYPE,
} from '../../const/VideoReactionDictionary';
import { TextInput } from '../../style/TextInput';
// types
import { PlaybackData } from '../../types';
// queries
import { usePostReactionCommentsMutation } from '../../queries/reactions/postReactionCommentsMutation';
// utils
import { formatDurationToSeconds, formatTimeToString } from '../../utils/date';
import { Button } from 'react-covideo-common';
import { Gap } from 'components/layout';
import { useGTMAnalytics } from 'hooks/GTM/useGTMAnalytics';
import { CTAInteractionType } from 'hooks/GTM/actions/CTA/interaction';
import { useTranslation } from 'react-i18next';

interface WrapperProps {
  height?: string;
}
const Wrapper = styled('div')<WrapperProps>`
  box-sizing: border-box;
  background: #ffffff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-bottom: 22px;
  border: 1px solid #eeeff2;
  border-radius: 6px;
  height: 48px;
  min-width: 620px;
  max-width: 100%;

  padding: 6px;

  & * {
    box-sizing: border-box;
  }

  input {
    margin: 0 8px 0 0;
  }

  @media screen and (max-width: 768px) {
    width: 620px;
    min-width: unset;
  }

  @media screen and (max-width: 676px) {
    width: 100%;
  }
`;

const ReactionButtonWrapper = styled('div')`
  display: flex;
  align-items: center;
`;
const ReactionButton = styled('button')`
  background: transparent;
  cursor: pointer;
  border: 0;
  margin: 0 8px;
  padding: 0;
  position: relative;
  &:hover {
    transform: scale(2) perspective(1px) translateY(-2px);
    &::before {
      content: '';
      --ss: 14px;
      width: var(--ss);
      height: var(--ss);
      background: rgba(39, 42, 50, 0.1);
      display: block;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 2px;
      z-index: -1;
      border-radius: 100%;
    }
  }

  @media screen and (max-width: 320px) {
    margin: 0 4px;
  }
`;

const COMMENT_MAX_CHARACTERS = 100;

const CommentCharacterCounter = styled.div<{ n: number; mobile?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  color: ${({ n, theme }) =>
    n > COMMENT_MAX_CHARACTERS
      ? theme.colors.danger[100]
      : theme.colors.neutral[60]};
  ${({ mobile }) =>
    mobile &&
    css`
      margin: 0 auto 12px auto;
    `}
`;

enum STEP {
  DEFAULT,
  INPUT_NAME,
  INPUT_COMMENT,
}

type Props = {
  reactionCommentsEnable?: boolean;
  playbackData?: PlaybackData;
  videoRef: MutableRefObject<HTMLVideoElement | undefined>;
};

type MobileReactionsProps = {
  inputType: 'name' | 'comment';
};

type ActionButtonProps = {
  inputType: 'name' | 'comment';
};

export default function ReactionAndComment(props: Props) {
  const { trackGTMCTAInteractionEvent } = useGTMAnalytics();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { shortUrl } = useParams();
  const { playbackData, videoRef } = props;
  const reactionCommentsEnable = !!playbackData?.reactionsEnabled;
  const currentTime = videoRef?.current?.currentTime;
  const erid = playbackData?.erid;
  const [comment, setComment] = React.useState('');
  const [reactionTypeClicked, setReactionTypeClicked] = useState<any>(null);
  const [reactionId, setReactionId] = useState<any>(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [customerName, setCustomerName] = useState(
    playbackData?.repairOrder?.firstName ||
      localStorage?.getItem('customerName')
  );

  const mainBgCommentColor = playbackData?.playerDesign.playerBackgroundColor;
  const mainTextCommentColor =
    playbackData?.playerDesign.playerIconsAndTextColor;

  const [step, setStep] = useState(STEP.DEFAULT);
  React.useEffect(() => {
    const currentVideo = videoRef.current;
    if (step === STEP.INPUT_COMMENT || step === STEP.INPUT_NAME) {
      currentVideo?.pause();
      return;
    }
  }, [step]);

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    setIsMobileDevice(window.innerWidth < 720);
  };

  const addReactionMutation = usePostReactionCommentsMutation();

  const addCommentReaction = async (
    reactionType: VIDEO_REACTION_TYPE,
    reactionId: string | number,
    comment: string
  ) => {
    const playbackPositionVal = videoRef?.current?.currentTime
      ? formatDurationToSeconds(videoRef?.current?.currentTime)
      : 1;
    let payload = {
      type: reactionType,
      playbackPosition: playbackPositionVal,
      name: localStorage?.getItem('customerName') || undefined,
      visitorCode: localStorage?.getItem('visitorCode'),
      emoticonId: '',
      text: '',
    };
    if (reactionType === VIDEO_REACTION_TYPE.EMOTE) {
      payload = { ...payload, emoticonId: reactionId.toString() };
    }
    if (reactionType === VIDEO_REACTION_TYPE.COMMENT) {
      payload = { ...payload, text: comment };
    }
    return await addReactionMutation.mutateAsync({ data: payload });
  };

  //Function helps to change the steps of flow according to information provided.
  const changeStepHandler = async (type: any) => {
    const reactionName = localStorage?.getItem('customerName') || customerName;
    localStorage.setItem('customerName', reactionName || '');
    setReactionTypeClicked(type);
    if (type === VIDEO_REACTION_TYPE.EMOTE) {
      await addCommentReaction(VIDEO_REACTION_TYPE.EMOTE, reactionId, comment);
      resetStep();
      return;
    }
    if ((step === STEP.DEFAULT && reactionName) || step === STEP.INPUT_NAME) {
      setStep(STEP.INPUT_COMMENT);
    } else if (step === STEP.DEFAULT && !reactionName) {
      setStep(STEP.INPUT_NAME);
    }
  };

  //Function use to reset all the state values to default when called.
  const resetStep = () => {
    setComment('');
    setCustomerName('');
    setStep(STEP.DEFAULT);
    setReactionTypeClicked(0);
  };

  //Function will be call when user click on comment button in step 3.
  const addComment = () => {
    addCommentReaction(VIDEO_REACTION_TYPE.COMMENT, '', comment);
    resetStep();
  };

  // Function will call when user click on any emoticons icons.
  const reactionIconClicked = async (id: string | number) => {
    setReactionTypeClicked(VIDEO_REACTION_TYPE.EMOTE);
    setReactionId(id);
    if (localStorage?.getItem('customerName')) {
      await addCommentReaction(VIDEO_REACTION_TYPE.EMOTE, id, comment);
      resetStep();
    } else {
      setStep(STEP.INPUT_NAME);
    }
  };

  const ActionButton = ({ inputType }: ActionButtonProps) => {
    const isComment = inputType === 'comment';
    if (isComment) {
      return (
        <Button
          text={t('Comment')}
          disabled={!comment || comment.length > COMMENT_MAX_CHARACTERS}
          onClick={() => {
            addComment();
          }}
          variant='white'
          style={{
            color: mainTextCommentColor || '#fff',
            backgroundColor: mainBgCommentColor || colors.primary[100],
            width: '100%',
          }}
        />
      );
    }

    return (
      <Button
        variant='white'
        onClick={() =>
          reactionTypeClicked === 2
            ? changeStepHandler(2)
            : changeStepHandler(0)
        }
        text={reactionTypeClicked === 2 ? t('Post Reaction') : t('Continue')}
        disabled={!customerName}
        style={{
          backgroundColor: mainBgCommentColor || colors.primary[100],
          color: mainTextCommentColor || '#fff',
          width: '100%',
        }}
      />
    );
  };

  const MobileReactions = ({ inputType }: MobileReactionsProps) => {
    return (
      <>
        {comment.length > COMMENT_MAX_CHARACTERS && (
          <CommentCharacterCounter
            n={comment.length}
            mobile={true}
          >{`${comment.length}/${COMMENT_MAX_CHARACTERS}`}</CommentCharacterCounter>
        )}
        <Gap flexDirection='row' flexWrap='nowrap' width='100%'>
          <Button
            variant='white'
            onClick={() => resetStep()}
            text={t('Cancel')}
            style={{
              backgroundColor: colors.neutral[20],
              color: mainBgCommentColor || colors.primary[100],
              width: '100%',
            }}
          />
          <ActionButton inputType={inputType} />
        </Gap>
      </>
    );
  };

  if (!erid && !reactionCommentsEnable) {
    return null;
  }

  return (
    <>
      {isMobileDevice && reactionCommentsEnable && (
        <Wrapper className='reactions-wrapper'>
          <ReactionButtonWrapper>
            {Object.values(VideoReactionsDictionary).map(
              (reaction: any, index: number) => (
                <ReactionButton
                  key={index}
                  onClick={() => reactionIconClicked(reaction?.id)}
                >
                  {reaction.icon}
                </ReactionButton>
              )
            )}
          </ReactionButtonWrapper>
        </Wrapper>
      )}
      <Wrapper height={'fit-content'} className='reactions-wrapper'>
        {step === STEP.DEFAULT && (
          <>
            {!isMobileDevice && reactionCommentsEnable && (
              <ReactionButtonWrapper>
                {Object.values(VideoReactionsDictionary).map(
                  (reaction: any, index: number) => (
                    <ReactionButton
                      key={index}
                      onClick={() => reactionIconClicked(reaction?.id)}
                    >
                      {reaction.icon}
                    </ReactionButton>
                  )
                )}
              </ReactionButtonWrapper>
            )}
            {reactionCommentsEnable && (
              <Button
                icon={<MdComment size={20} />}
                onClick={() => changeStepHandler(VIDEO_REACTION_TYPE.COMMENT)}
                text={t('Comment')}
                variant='white'
                style={{ border: 'none' }}
              />
            )}
            {!!erid && (
              <Button
                className='font-roboto'
                onClick={() => {
                  trackGTMCTAInteractionEvent?.({
                    type: CTAInteractionType.REPLY_WITH_VIDEO,
                  });
                  window.open(
                    `/${shortUrl}/video-reply/${erid}`,
                    '_blank noopener noreferrer'
                  );
                }}
                text={t('Reply with video')}
                icon={<MdVideocam size={20} />}
                style={{ border: 'none', backgroundColor: 'white' }}
                variant='destructive'
              />
            )}
          </>
        )}
        {step === STEP.INPUT_NAME && (
          <>
            <TextInput
              value={customerName}
              placeholder={t('Enter your name to be able to react and comment')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCustomerName?.(event.target.value)
              }
            />
            {isMobileDevice ? (
              <MobileReactions inputType='name' />
            ) : (
              <Gap flexWrap='nowrap' gap='4px'>
                <Button
                  variant='white'
                  text={
                    reactionTypeClicked === 2
                      ? t('Post Reaction')
                      : t('Continue')
                  }
                  disabled={!customerName}
                  onClick={() =>
                    reactionTypeClicked === 2
                      ? changeStepHandler(2)
                      : changeStepHandler(0)
                  }
                  style={{
                    backgroundColor: mainBgCommentColor || colors.primary[100],
                    color: mainTextCommentColor || '#fff',
                  }}
                />
                <Button
                  icon={<MdClose size={20} />}
                  onClick={() => resetStep()}
                  variant='transparent'
                />
              </Gap>
            )}
          </>
        )}

        {step === STEP.INPUT_COMMENT && (
          <>
            <TextInput
              value={comment}
              placeholder={t('Type your comment here')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setComment(event.target.value)
              }
              width={'80%'}
              marginLeft={'8px'}
            />
            {isMobileDevice ? (
              <MobileReactions inputType='comment' />
            ) : (
              <Gap flexWrap='nowrap' gap='4px'>
                <CommentCharacterCounter
                  n={comment.length}
                >{`${comment.length}/${COMMENT_MAX_CHARACTERS}`}</CommentCharacterCounter>
                <Button
                  onClick={() => {
                    addComment();
                  }}
                  text={`Comment at ${
                    currentTime ? formatTimeToString(currentTime) : '00:00'
                  }`}
                  disabled={!comment || comment.length > COMMENT_MAX_CHARACTERS}
                  style={{
                    backgroundColor: mainBgCommentColor || colors.primary[100],
                    color: mainTextCommentColor || '#fff',
                    flexShrink: 0,
                  }}
                  variant='white'
                />
                <Button
                  icon={<MdClose size={20} />}
                  variant='transparent'
                  onClick={() => resetStep()}
                />
              </Gap>
            )}
          </>
        )}
      </Wrapper>
    </>
  );
}
